
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  createSite as createSiteAPI,
  deleteSite as deleteSiteAPI,
  getUserSites,
  restoreSite as restoreSiteAPI,
  updateSite as updateSiteAPI,
} from "../services/api/siteService";
import {
  createUnit as createUnitAPI,
  deleteUnit as deleteUnitAPI,
  getSiteUnits as getSiteUnitsAPI,
  getUserUnits,
  restoreUnit as restoreUnitAPI,
  updateUnit as updateUnitAPI,
} from "../services/api/unitService";

const SiteUnitContext = createContext();

/**
 * Normalizes an identifier for safe equality comparisons across string and numeric IDs.
 *
 * @param {string|number|null|undefined} value - Identifier value to normalize.
 * @returns {string|null} Comparable string identifier, or null when no value is provided.
 */
const toComparableId = value => (value == null ? null : String(value));

/**
 * Converts a detailed Unit object into the lightweight Unit summary stored inside Site state.
 *
 * @param {Object} unit - Unit returned by the API.
 * @returns {Object} Normalized Unit summary safe for Site navigation and summary consumers.
 */
const toUnitSummary = unit => ({
  id: unit.id,
  unit_id: unit.unit_id,
  unit_name: unit.unit_name,
  nickname: unit.nickname ?? null,
  status: unit.status,
  version: unit.version,
});

/**
 * Determines whether a Unit belongs to a Site using either a known Unit-ID set or detailed ownership fields.
 *
 * @param {Object|null|undefined} unit - Unit summary or detailed Unit record.
 * @param {string|number} siteId - Site identifier being evaluated.
 * @param {Set<string>} [knownUnitIds] - Unit IDs already known from the Site summary.
 * @returns {boolean} True when the Unit is known to belong to the Site.
 */
const unitBelongsToSite = (unit, siteId, knownUnitIds = new Set()) => {
  const comparableUnitId = toComparableId(unit?.id);
  if (comparableUnitId != null && knownUnitIds.has(comparableUnitId)) {
    return true;
  }

  const comparableSiteId = toComparableId(siteId);
  const ownerSiteId = toComparableId(unit?.site_id ?? unit?.site?.id);
  return comparableSiteId != null && ownerSiteId === comparableSiteId;
};

/**
 * Returns the shared Site/Unit context.
 *
 * @returns {Object} Site and Unit state, request state, selectors, and mutation helpers.
 * @throws {Error} When the hook is used outside SiteUnitProvider.
 */
export const useSiteUnit = () => {
  const context = useContext(SiteUnitContext);
  if (!context) {
    throw new Error("useSiteUnit must be used within a SiteUnitProvider");
  }
  return context;
};

/**
 * Provides authoritative Site state, derived Unit summaries, selection state, and race-safe reads.
 *
 * @param {Object} props - Provider properties.
 * @param {React.ReactNode} props.children - Descendant components that consume Site/Unit state.
 * @param {string|number|null|undefined} props.userKey - Stable authenticated-user identity used to reset state between accounts.
 * @returns {React.ReactElement} Site/Unit context provider.
 */
export const SiteUnitProvider = ({ children, userKey }) => {
  const siteListRequestIdRef = useRef(0);
  const siteListAbortRef = useRef(null);
  const unitListRequestIdRef = useRef(0);
  const unitListAbortRef = useRef(null);
  const selectedSiteIdRef = useRef(null);
  const sitesRef = useRef([]);
  const unitsRef = useRef([]);
  const siteSelectionInitializedRef = useRef(false);
  const unitsScopeRef = useRef({ mode: "summary", siteId: null });
  const activeUnitReadScopeRef = useRef(null);
  const contextGenerationRef = useRef(0);

  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [sites, setSites] = useState([]);
  const [units, setUnits] = useState([]);

  const [sitesLoading, setSitesLoading] = useState(false);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [mutationCount, setMutationCount] = useState(0);
  const [sitesLoaded, setSitesLoaded] = useState(false);

  const [sitesError, setSitesError] = useState(null);
  const [unitsError, setUnitsError] = useState(null);
  const [mutationError, setMutationError] = useState(null);

  const allUnits = useMemo(() => sites.flatMap(site => site.units || []), [sites]);

  const selectedSite = useMemo(() => {
    if (selectedSiteId == null) {
      return null;
    }
    const selectedId = toComparableId(selectedSiteId);
    return sites.find(site => toComparableId(site.id) === selectedId) || null;
  }, [selectedSiteId, sites]);

  const loading = sitesLoading || unitsLoading || mutationCount > 0;
  const error = mutationError || sitesError || unitsError;

  useEffect(() => {
    selectedSiteIdRef.current = selectedSiteId;
  }, [selectedSiteId]);

  /**
   * Invalidates and aborts the active Site-list request, if one exists.
   *
   * Explicit invalidation clears Site loading immediately. A replacement read can
   * keep loading true so the replacement request owns the request-family state.
   *
   * @param {Object} [options] - Abort behavior.
   * @param {boolean} [options.clearLoading=true] - Whether to clear Site loading after invalidation.
   * @returns {void}
   */
  const abortSiteListRead = useCallback(({ clearLoading = true } = {}) => {
    siteListRequestIdRef.current += 1;

    if (siteListAbortRef.current) {
      siteListAbortRef.current.abort();
      siteListAbortRef.current = null;
    }

    if (clearLoading) {
      setSitesLoading(false);
    }
  }, []);

  /**
   * Invalidates and aborts the active Unit-list request, if one exists.
   *
   * Explicit invalidation clears Unit loading immediately. A replacement read can
   * keep loading true so the replacement request owns the request-family state.
   *
   * @param {Object} [options] - Abort behavior.
   * @param {boolean} [options.clearLoading=true] - Whether to clear Unit loading after invalidation.
   * @returns {void}
   */
  const abortUnitListRead = useCallback(({ clearLoading = true } = {}) => {
    unitListRequestIdRef.current += 1;

    if (unitListAbortRef.current) {
      unitListAbortRef.current.abort();
      unitListAbortRef.current = null;
    }

    activeUnitReadScopeRef.current = null;

    if (clearLoading) {
      setUnitsLoading(false);
    }
  }, []);

  /**
   * Marks the beginning of a Site/Unit mutation and clears the previous mutation error.
   *
   * @returns {void}
   */
  const beginMutation = useCallback(() => {
    setMutationCount(count => count + 1);
    setMutationError(null);
  }, []);

  /**
   * Marks completion of one Site/Unit mutation without allowing the pending counter below zero.
   *
   * @returns {void}
   */
  const endMutation = useCallback(() => {
    setMutationCount(count => Math.max(0, count - 1));
  }, []);

  /**
   * Patches one authoritative Site summary from a safe owned 409 response.
   *
   * Unit membership is intentionally preserved because Batch 3C protects the Site row itself;
   * child membership/lifecycle semantics remain Batch 3D work.
   *
   * @param {string|number} siteId - Site identifier being reconciled.
   * @param {Object|null|undefined} currentSite - Current owned Site returned by the backend.
   * @returns {void}
   */
  const patchSiteConflictSnapshot = useCallback((siteId, currentSite) => {
    if (!currentSite) {
      return;
    }

    const targetId = toComparableId(siteId);
    setSites(previousSites =>
      previousSites.map(site =>
        toComparableId(site.id) === targetId
          ? {
              ...site,
              ...currentSite,
              units: site.units || [],
            }
          : site
      )
    );
  }, []);

  /**
   * Patches one Unit across authoritative Site summaries and any active detailed Unit state.
   *
   * @param {string|number} unitId - Unit identifier being reconciled.
   * @param {Object|null|undefined} currentUnit - Current owned Unit returned by the backend.
   * @returns {void}
   */
  const patchUnitConflictSnapshot = useCallback((unitId, currentUnit) => {
    if (!currentUnit) {
      return;
    }

    const targetId = toComparableId(unitId);
    const unitSummary = toUnitSummary(currentUnit);

    setSites(previousSites =>
      previousSites.map(site => {
        const siteUnits = site.units || [];
        const containsUnit = siteUnits.some(unit => toComparableId(unit.id) === targetId);
        if (!containsUnit) {
          return site;
        }

        return {
          ...site,
          units: siteUnits.map(unit =>
            toComparableId(unit.id) === targetId ? { ...unit, ...unitSummary } : unit
          ),
        };
      })
    );

    setUnits(previousUnits =>
      previousUnits.map(unit =>
        toComparableId(unit.id) === targetId
          ? unitsScopeRef.current.mode === "summary"
            ? { ...unit, ...unitSummary }
            : { ...unit, ...currentUnit }
          : unit
      )
    );

    setSelectedUnit(current =>
      current && toComparableId(current.id) === targetId
        ? { ...current, ...currentUnit }
        : current
    );
  }, []);

  /**
   * Removes a Site from local authoritative state after the backend confirms it is gone/inaccessible.
   *
   * This is targeted reconciliation only; it does not define dependent-data deletion semantics.
   *
   * @param {string|number} siteId - Missing Site identifier.
   * @returns {void}
   */
  const removeMissingSiteSnapshot = useCallback(siteId => {
    const targetId = toComparableId(siteId);
    setSites(previousSites =>
      previousSites.filter(site => toComparableId(site.id) !== targetId)
    );

    if (toComparableId(selectedSiteIdRef.current) === targetId) {
      selectedSiteIdRef.current = null;
      setSelectedSiteId(null);
      setSelectedUnit(null);
      unitsScopeRef.current = { mode: "summary", siteId: null };
    }
  }, []);

  /**
   * Removes one missing Unit from local summary/detail state after a safe backend 404.
   *
   * @param {string|number} unitId - Missing Unit identifier.
   * @returns {void}
   */
  const removeMissingUnitSnapshot = useCallback(unitId => {
    const targetId = toComparableId(unitId);

    setSites(previousSites =>
      previousSites.map(site => {
        const siteUnits = site.units || [];
        if (!siteUnits.some(unit => toComparableId(unit.id) === targetId)) {
          return site;
        }

        return {
          ...site,
          units: siteUnits.filter(unit => toComparableId(unit.id) !== targetId),
        };
      })
    );
    setUnits(previousUnits =>
      previousUnits.filter(unit => toComparableId(unit.id) !== targetId)
    );
    setSelectedUnit(current =>
      current && toComparableId(current.id) === targetId ? null : current
    );
  }, []);

  useEffect(() => {
    sitesRef.current = sites;

    if (unitsScopeRef.current.mode !== "summary") {
      return;
    }

    if (selectedSiteId == null) {
      setUnits(sites.flatMap(site => site.units || []));
      return;
    }

    const selected = sites.find(
      site => toComparableId(site.id) === toComparableId(selectedSiteId)
    );
    setUnits(selected?.units || []);
  }, [sites, selectedSiteId]);

  useEffect(() => {
    unitsRef.current = units;
  }, [units]);

  /**
   * Loads the authenticated user's Sites and lightweight Unit summaries with stale-response protection.
   *
   * @returns {Promise<void>} Resolves after the current Site-list request has been processed or ignored.
   */
  const loadUserSites = useCallback(async () => {
    abortSiteListRead({ clearLoading: false });

    const requestId = siteListRequestIdRef.current;
    const controller = new AbortController();
    siteListAbortRef.current = controller;

    setSitesLoading(true);
    setSitesLoaded(false);
    setSitesError(null);

    try {
      const response = await getUserSites({ signal: controller.signal });

      if (controller.signal.aborted || requestId !== siteListRequestIdRef.current) {
        return;
      }

      if (!response?.success) {
        throw new Error(response?.message || "Failed to load sites");
      }

      const nextSites = Array.isArray(response.data) ? response.data : [];
      setSites(nextSites);

      let nextSelectedSiteId = selectedSiteIdRef.current;
      const hadSelectedSite = nextSelectedSiteId != null;
      const selectedStillExists =
        hadSelectedSite &&
        nextSites.some(site => toComparableId(site.id) === toComparableId(nextSelectedSiteId));
      const selectedSiteDisappeared = hadSelectedSite && !selectedStillExists;

      if (!selectedStillExists) {
        if (!siteSelectionInitializedRef.current && nextSites.length > 0) {
          nextSelectedSiteId = nextSites[0].id;
        } else {
          nextSelectedSiteId = null;
        }

        selectedSiteIdRef.current = nextSelectedSiteId;
        setSelectedSiteId(nextSelectedSiteId);
        setSelectedUnit(null);

        if (selectedSiteDisappeared) {
          abortUnitListRead();
          setUnitsError(null);
          unitsScopeRef.current = {
            mode: "summary",
            siteId: toComparableId(nextSelectedSiteId),
          };
        }
      }

      if (unitsScopeRef.current.mode === "summary") {
        const nextActiveSummaryUnits =
          nextSelectedSiteId == null
            ? nextSites.flatMap(site => site.units || [])
            : nextSites.find(
                site => toComparableId(site.id) === toComparableId(nextSelectedSiteId)
              )?.units || [];

        setUnits(nextActiveSummaryUnits);
        setSelectedUnit(current => {
          if (!current || selectedSiteDisappeared) {
            return null;
          }

          return (
            nextActiveSummaryUnits.find(
              unit => toComparableId(unit.id) === toComparableId(current.id)
            ) || null
          );
        });
      }
    } catch (requestError) {
      const cancelled = controller.signal.aborted || requestError?.code === "ERR_CANCELED";
      if (cancelled || requestId !== siteListRequestIdRef.current) {
        return;
      }

      console.error("Error loading sites:", requestError);
      setSitesError("Failed to load sites");
    } finally {
      if (requestId === siteListRequestIdRef.current) {
        if (siteListAbortRef.current === controller) {
          siteListAbortRef.current = null;
        }
        setSitesLoading(false);
        setSitesLoaded(true);
      }
    }
  }, [abortSiteListRead, abortUnitListRead]);

  /**
   * Loads detailed Units for one Site and commits them only when the request is still current.
   *
   * @param {string|number} siteId - Site identifier whose detailed Units should be loaded.
   * @returns {Promise<Object[]>} Detailed Unit records for the Site, or an empty array when the request is cancelled.
   */
  const loadSiteUnits = useCallback(
    async siteId => {
      if (!siteId) {
        return [];
      }

      abortUnitListRead({ clearLoading: false });

      const requestId = unitListRequestIdRef.current;
      const targetSiteId = toComparableId(siteId);
      const controller = new AbortController();
      unitListAbortRef.current = controller;
      activeUnitReadScopeRef.current = { mode: "detail", siteId: targetSiteId };

      setUnitsLoading(true);
      setUnitsError(null);

      try {
        const response = await getSiteUnitsAPI(siteId, { signal: controller.signal });

        if (controller.signal.aborted || requestId !== unitListRequestIdRef.current) {
          return [];
        }

        if (!response?.success) {
          throw new Error(response?.message || "Failed to load units");
        }

        const nextUnits = Array.isArray(response.data) ? response.data : [];

        if (toComparableId(selectedSiteIdRef.current) === targetSiteId) {
          unitsScopeRef.current = { mode: "detail", siteId: targetSiteId };
          setUnits(nextUnits);
          setSelectedUnit(current => {
            if (!current) {
              return null;
            }

            return (
              nextUnits.find(unit => toComparableId(unit.id) === toComparableId(current.id)) || null
            );
          });
        }

        return nextUnits;
      } catch (requestError) {
        const cancelled = controller.signal.aborted || requestError?.code === "ERR_CANCELED";
        if (cancelled || requestId !== unitListRequestIdRef.current) {
          return [];
        }

        console.error("Error loading site units:", requestError);
        setUnitsError("Failed to load units");
        throw requestError;
      } finally {
        if (requestId === unitListRequestIdRef.current) {
          if (unitListAbortRef.current === controller) {
            unitListAbortRef.current = null;
            activeUnitReadScopeRef.current = null;
          }
          setUnitsLoading(false);
        }
      }
    },
    [abortUnitListRead]
  );

  /**
   * Loads detailed Units across all Sites while protecting the shared Unit state from stale responses.
   *
   * @returns {Promise<Object[]>} Detailed Unit records, or an empty array when the request is cancelled.
   */
  const loadAllUnits = useCallback(async () => {
    abortUnitListRead({ clearLoading: false });

    const requestId = unitListRequestIdRef.current;
    const controller = new AbortController();
    unitListAbortRef.current = controller;
    activeUnitReadScopeRef.current = { mode: "all-detail", siteId: null };

    setUnitsLoading(true);
    setUnitsError(null);

    try {
      const response = await getUserUnits(null, { signal: controller.signal });

      if (controller.signal.aborted || requestId !== unitListRequestIdRef.current) {
        return [];
      }

      if (!response?.success) {
        throw new Error(response?.message || "Failed to load units");
      }

      const nextUnits = Array.isArray(response.data) ? response.data : [];

      if (selectedSiteIdRef.current == null) {
        unitsScopeRef.current = { mode: "all-detail", siteId: null };
        setUnits(nextUnits);
      }

      return nextUnits;
    } catch (requestError) {
      const cancelled = controller.signal.aborted || requestError?.code === "ERR_CANCELED";
      if (cancelled || requestId !== unitListRequestIdRef.current) {
        return [];
      }

      console.error("Error loading units:", requestError);
      setUnitsError("Failed to load units");
      throw requestError;
    } finally {
      if (requestId === unitListRequestIdRef.current) {
        if (unitListAbortRef.current === controller) {
          unitListAbortRef.current = null;
          activeUnitReadScopeRef.current = null;
        }
        setUnitsLoading(false);
      }
    }
  }, [abortUnitListRead]);

  /**
   * Returns whether a Site-list request currently owns an active AbortController.
   *
   * @returns {boolean} True when a Site-list read is currently in flight.
   */
  const hasActiveSiteListRead = useCallback(() => Boolean(siteListAbortRef.current), []);

  /**
   * Finds the owning Site for a Unit from available Site summaries or detailed Unit state.
   *
   * @param {string|number} unitId - Unit identifier.
   * @returns {string|null} Comparable Site identifier when known.
   */
  const getUnitSiteIdFromState = useCallback(unitId => {
    const comparableUnitId = toComparableId(unitId);

    for (const site of sitesRef.current) {
      if ((site.units || []).some(unit => toComparableId(unit.id) === comparableUnitId)) {
        return toComparableId(site.id);
      }
    }

    const detailedUnit = unitsRef.current.find(unit => toComparableId(unit.id) === comparableUnitId);
    return toComparableId(detailedUnit?.site_id ?? detailedUnit?.site?.id);
  }, []);

  /**
   * Captures an active Unit read only when a Unit mutation can make that read stale.
   *
   * @param {string|number|null} mutatedSiteId - Site affected by the Unit mutation when known.
   * @returns {{mode: string, siteId: string|null}|null} Relevant active Unit read scope.
   */
  const getRelevantActiveUnitReadScope = useCallback(mutatedSiteId => {
    const activeScope = activeUnitReadScopeRef.current;
    if (!activeScope) {
      return null;
    }

    if (activeScope.mode === "all-detail") {
      return { ...activeScope };
    }

    const comparableMutatedSiteId = toComparableId(mutatedSiteId);
    if (
      activeScope.mode === "detail" &&
      (comparableMutatedSiteId == null || activeScope.siteId === comparableMutatedSiteId)
    ) {
      return { ...activeScope };
    }

    return null;
  }, []);

  /**
   * Starts one authoritative Site replacement read only when a mutation interrupted an active Site read.
   *
   * @param {boolean} shouldReconcile - Whether the mutation invalidated an active Site read.
   * @param {number} mutationGeneration - Account/context generation captured by the mutation.
   * @returns {void}
   */
  const reconcileSitesAfterMutationIfNeeded = useCallback(
    (shouldReconcile, mutationGeneration) => {
      if (!shouldReconcile || mutationGeneration !== contextGenerationRef.current) {
        return;
      }

      void loadUserSites();
    },
    [loadUserSites]
  );

  /**
   * Returns whether a previously active Unit read scope is still relevant to the current selection.
   *
   * @param {{mode: string, siteId: string|null}|null} scope - Unit read scope captured before invalidation.
   * @returns {boolean} True when that scope should still be reconciled.
   */
  const isUnitReadScopeStillRelevant = useCallback(scope => {
    if (!scope) {
      return false;
    }

    if (scope.mode === "detail") {
      return toComparableId(selectedSiteIdRef.current) === toComparableId(scope.siteId);
    }

    if (scope.mode === "all-detail") {
      return selectedSiteIdRef.current == null;
    }

    return false;
  }, []);

  /**
   * Starts one replacement Unit read only when an invalidated detail scope is still relevant.
   *
   * Reconciliation errors remain owned by Unit read state so a successful mutation
   * is not reclassified as a mutation failure.
   *
   * @param {{mode: string, siteId: string|null}|null} scope - Invalidated Unit read scope.
   * @param {number} mutationGeneration - Account/context generation captured by the mutation.
   * @returns {void}
   */
  const reconcileUnitsAfterMutationIfNeeded = useCallback(
    (scope, mutationGeneration) => {
      if (
        mutationGeneration !== contextGenerationRef.current ||
        !isUnitReadScopeStillRelevant(scope)
      ) {
        return;
      }

      const reconciliation =
        scope.mode === "detail" ? loadSiteUnits(scope.siteId) : loadAllUnits();

      void reconciliation.catch(() => {});
    },
    [isUnitReadScopeStillRelevant, loadAllUnits, loadSiteUnits]
  );

  useEffect(() => {
    contextGenerationRef.current += 1;

    abortSiteListRead();
    abortUnitListRead();

    setSites([]);
    setUnits([]);
    setSelectedSiteId(null);
    selectedSiteIdRef.current = null;
    setSelectedUnit(null);
    setSitesError(null);
    setUnitsError(null);
    setMutationError(null);
    setMutationCount(0);
    setSitesLoaded(false);
    siteSelectionInitializedRef.current = false;
    unitsScopeRef.current = { mode: "summary", siteId: null };
    activeUnitReadScopeRef.current = null;

    if (!userKey) {
      setSitesLoading(false);
      setUnitsLoading(false);
      setSitesLoaded(true);
      return undefined;
    }

    loadUserSites();

    return () => {
      contextGenerationRef.current += 1;
      abortSiteListRead({ clearLoading: false });
      abortUnitListRead({ clearLoading: false });
    };
  }, [userKey, loadUserSites, abortSiteListRead, abortUnitListRead]);

  /**
   * Selects an authoritative Site, or the all-Sites context when null is requested.
   *
   * Unknown/stale non-null Site objects are ignored rather than trusting their nested Unit data.
   *
   * @param {Object|null} site - Authoritative Site to select, or null to select all Sites.
   * @returns {void}
   */
  const selectSite = useCallback(
    site => {
      if (site === null) {
        abortUnitListRead();
        siteSelectionInitializedRef.current = true;
        selectedSiteIdRef.current = null;
        setSelectedSiteId(null);
        setSelectedUnit(null);
        setUnitsError(null);
        unitsScopeRef.current = { mode: "summary", siteId: null };
        setUnits(sites.flatMap(item => item.units || []));
        return;
      }

      if (!site || site.id == null) {
        return;
      }

      const requestedSiteId = toComparableId(site.id);
      const authoritativeSite = sites.find(
        item => toComparableId(item.id) === requestedSiteId
      );

      if (!authoritativeSite) {
        return;
      }

      abortUnitListRead();
      siteSelectionInitializedRef.current = true;
      selectedSiteIdRef.current = authoritativeSite.id;
      setSelectedSiteId(authoritativeSite.id);
      setSelectedUnit(null);
      setUnitsError(null);
      unitsScopeRef.current = {
        mode: "summary",
        siteId: toComparableId(authoritativeSite.id),
      };
      setUnits(authoritativeSite.units || []);
    },
    [abortUnitListRead, sites]
  );

  /**
   * Selects one Unit in the current Site/Unit context.
   *
   * @param {Object|null} unit - Unit to select, or null to clear Unit selection.
   * @returns {void}
   */
  const selectUnit = useCallback(unit => {
    setSelectedUnit(unit || null);
  }, []);

  /**
   * Returns the status of a Unit currently represented in shared Unit state.
   *
   * @param {string|number} unitId - Unit identifier.
   * @returns {string} Unit status, or "Unknown" when the Unit is not present.
   */
  const getUnitStatus = useCallback(
    unitId => {
      const unit = units.find(item => toComparableId(item.id) === toComparableId(unitId));
      return unit ? unit.status : "Unknown";
    },
    [units]
  );

  /**
   * Returns the lightweight Unit summaries owned by a Site.
   *
   * @param {string|number} siteId - Site identifier.
   * @returns {Object[]} Unit summaries for the Site.
   */
  const getSiteUnits = useCallback(
    siteId => {
      const site = sites.find(item => toComparableId(item.id) === toComparableId(siteId));
      return site ? site.units || [] : [];
    },
    [sites]
  );

  /**
   * Returns all Unit summaries derived from authoritative Site state.
   *
   * @returns {Object[]} Flattened Unit summary collection.
   */
  const getAllUnits = useCallback(() => allUnits, [allUnits]);

  /**
   * Creates a Site, updates authoritative state locally, and reconciles only if an active Site read was interrupted.
   *
   * @param {Object} siteData - Site values accepted by the Site API.
   * @returns {Promise<Object|null>} Newly created Site, or null when the originating account context is no longer current.
   */
  const createSite = useCallback(
    async siteData => {
      const mutationGeneration = contextGenerationRef.current;
      beginMutation();

      try {
        const response = await createSiteAPI(siteData);
        if (!response?.success) {
          throw new Error(response?.message || "Failed to create site");
        }

        if (mutationGeneration !== contextGenerationRef.current) {
          return null;
        }

        const hadActiveSiteRead = hasActiveSiteListRead();
        if (hadActiveSiteRead) {
          abortSiteListRead();
        }

        const newSite = {
          ...response.data,
          units: Array.isArray(response.data?.units) ? response.data.units.map(toUnitSummary) : [],
        };

        setSites(previousSites => [
          newSite,
          ...previousSites.filter(site => toComparableId(site.id) !== toComparableId(newSite.id)),
        ]);

        reconcileSitesAfterMutationIfNeeded(hadActiveSiteRead, mutationGeneration);
        return newSite;
      } catch (mutationFailure) {
        if (mutationGeneration !== contextGenerationRef.current) {
          return null;
        }

        console.error("Error creating site:", mutationFailure);
        setMutationError("Failed to create site");
        throw mutationFailure;
      } finally {
        if (mutationGeneration === contextGenerationRef.current) {
          endMutation();
        }
      }
    },
    [
      abortSiteListRead,
      beginMutation,
      endMutation,
      hasActiveSiteListRead,
      reconcileSitesAfterMutationIfNeeded,
    ]
  );

  /**
   * Deletes a Site, immediately removes its Site/Unit state, and reconciles only interrupted relevant reads.
   *
   * Unit cleanup uses both known Site-summary Unit IDs and detailed Unit ownership fields so stale summaries
   * cannot leave deleted-Site Units in detailed frontend state.
   *
   * @param {string|number} siteId - Site identifier to delete.
   * @param {number} expectedVersion - Version captured with the delete target snapshot.
   * @returns {Promise<boolean>} True when deletion succeeds in the current account context; false for a stale account result.
   */
  const deleteSite = useCallback(
    async (siteId, expectedVersion) => {
      const mutationGeneration = contextGenerationRef.current;
      beginMutation();

      try {
        const response = await deleteSiteAPI(siteId, expectedVersion);
        if (!response?.success) {
          throw new Error(response?.message || "Failed to delete site");
        }

        if (mutationGeneration !== contextGenerationRef.current) {
          return false;
        }

        const deletingId = toComparableId(siteId);
        const hadActiveSiteRead = hasActiveSiteListRead();
        const activeUnitReadScope = getRelevantActiveUnitReadScope(deletingId);

        if (hadActiveSiteRead) {
          abortSiteListRead();
        }
        if (activeUnitReadScope) {
          abortUnitListRead();
        }

        const deletedSite = sitesRef.current.find(
          site => toComparableId(site.id) === deletingId
        );
        const deletedUnitIds = new Set(
          (deletedSite?.units || []).map(unit => toComparableId(unit.id))
        );
        const nextSites = sitesRef.current.filter(
          site => toComparableId(site.id) !== deletingId
        );
        const isDeletingSelectedSite =
          toComparableId(selectedSiteIdRef.current) === deletingId;

        setSites(nextSites);

        if (isDeletingSelectedSite) {
          selectedSiteIdRef.current = null;
          setSelectedSiteId(null);
          setSelectedUnit(null);
          setUnitsError(null);
          unitsScopeRef.current = { mode: "summary", siteId: null };
          setUnits(nextSites.flatMap(site => site.units || []));
        } else {
          setSelectedUnit(current =>
            current && unitBelongsToSite(current, deletingId, deletedUnitIds)
              ? null
              : current
          );

          if (unitsScopeRef.current.mode === "summary") {
            const activeSiteId = selectedSiteIdRef.current;

            if (activeSiteId == null) {
              setUnits(nextSites.flatMap(site => site.units || []));
            } else {
              const activeSite = nextSites.find(
                site => toComparableId(site.id) === toComparableId(activeSiteId)
              );
              setUnits(activeSite?.units || []);
            }
          } else {
            setUnits(currentUnits =>
              currentUnits.filter(
                unit => !unitBelongsToSite(unit, deletingId, deletedUnitIds)
              )
            );
          }
        }

        reconcileSitesAfterMutationIfNeeded(hadActiveSiteRead, mutationGeneration);
        reconcileUnitsAfterMutationIfNeeded(activeUnitReadScope, mutationGeneration);
        return true;
      } catch (mutationFailure) {
        if (mutationGeneration !== contextGenerationRef.current) {
          return false;
        }

        if (mutationFailure?.response?.status === 409) {
          patchSiteConflictSnapshot(siteId, mutationFailure.response?.data?.data);
        } else if (mutationFailure?.response?.status === 404) {
          removeMissingSiteSnapshot(siteId);
        }

        console.error("Error deleting site:", mutationFailure);
        setMutationError("Failed to delete site");
        throw mutationFailure;
      } finally {
        if (mutationGeneration === contextGenerationRef.current) {
          endMutation();
        }
      }
    },
    [
      abortSiteListRead,
      abortUnitListRead,
      beginMutation,
      endMutation,
      getRelevantActiveUnitReadScope,
      hasActiveSiteListRead,
      patchSiteConflictSnapshot,
      removeMissingSiteSnapshot,
      reconcileSitesAfterMutationIfNeeded,
      reconcileUnitsAfterMutationIfNeeded,
    ]
  );

  const restoreSite = useCallback(async siteId => {
    const mutationGeneration = contextGenerationRef.current;
    beginMutation();
    try {
      const response = await restoreSiteAPI(siteId);
      if (!response?.success) throw new Error(response?.message || "Failed to restore farm");
      if (mutationGeneration !== contextGenerationRef.current) return null;

      const restoredSite = {
        ...response.data,
        units: (response.data?.units || []).map(toUnitSummary),
      };
      const hadActiveSiteRead = hasActiveSiteListRead();
      if (hadActiveSiteRead) {
        abortSiteListRead();
      }

      setSites(previous => [
        restoredSite,
        ...previous.filter(site => toComparableId(site.id) !== toComparableId(restoredSite.id)),
      ]);
      reconcileSitesAfterMutationIfNeeded(hadActiveSiteRead, mutationGeneration);
      return restoredSite;
    } catch (error) {
      if (mutationGeneration === contextGenerationRef.current) {
        setMutationError("Failed to restore farm");
      }
      throw error;
    } finally {
      if (mutationGeneration === contextGenerationRef.current) endMutation();
    }
  }, [
    abortSiteListRead,
    beginMutation,
    endMutation,
    hasActiveSiteListRead,
    reconcileSitesAfterMutationIfNeeded,
  ]);

  /**
   * Updates a Site locally while preserving Unit summaries and reconciles only if an active Site read was interrupted.
   *
   * @param {string|number} siteId - Site identifier to update.
   * @param {Object} updatedSiteData - Site fields to persist.
   * @returns {Promise<Object|null>} Updated Site payload, or null when the originating account context is no longer current.
   */
  const updateSite = useCallback(
    async (siteId, updatedSiteData) => {
      const mutationGeneration = contextGenerationRef.current;
      beginMutation();

      try {
        const response = await updateSiteAPI(siteId, updatedSiteData);
        if (!response?.success) {
          throw new Error(response?.message || "Failed to update site");
        }

        if (mutationGeneration !== contextGenerationRef.current) {
          return null;
        }

        const hadActiveSiteRead = hasActiveSiteListRead();
        if (hadActiveSiteRead) {
          abortSiteListRead();
        }

        setSites(previousSites =>
          previousSites.map(site =>
            toComparableId(site.id) === toComparableId(siteId)
              ? {
                  ...site,
                  ...response.data,
                  units: site.units || [],
                }
              : site
          )
        );

        setSelectedUnit(currentUnit => {
          const currentSiteId = currentUnit?.site_id ?? currentUnit?.site?.id;
          if (toComparableId(currentSiteId) !== toComparableId(siteId)) {
            return currentUnit;
          }

          return {
            ...currentUnit,
            site: {
              ...(currentUnit.site || {}),
              ...response.data,
              units: currentUnit.site?.units || [],
            },
          };
        });

        reconcileSitesAfterMutationIfNeeded(hadActiveSiteRead, mutationGeneration);
        return response.data;
      } catch (mutationFailure) {
        if (mutationGeneration !== contextGenerationRef.current) {
          return null;
        }

        if (mutationFailure?.response?.status === 409) {
          patchSiteConflictSnapshot(siteId, mutationFailure.response?.data?.data);
        } else if (mutationFailure?.response?.status === 404) {
          removeMissingSiteSnapshot(siteId);
        }

        console.error("Error updating site:", mutationFailure);
        setMutationError("Failed to update site");
        throw mutationFailure;
      } finally {
        if (mutationGeneration === contextGenerationRef.current) {
          endMutation();
        }
      }
    },
    [
      abortSiteListRead,
      beginMutation,
      endMutation,
      hasActiveSiteListRead,
      patchSiteConflictSnapshot,
      removeMissingSiteSnapshot,
      reconcileSitesAfterMutationIfNeeded,
    ]
  );

  /**
   * Finds a Site in authoritative Site state.
   *
   * @param {string|number} siteId - Site identifier.
   * @returns {Object|undefined} Matching Site when present.
   */
  const getSiteById = useCallback(
    siteId => sites.find(site => toComparableId(site.id) === toComparableId(siteId)),
    [sites]
  );

  /**
   * Creates a Unit, updates local summary/detail state, and reconciles only read scopes made stale by the mutation.
   *
   * @param {string|number} siteId - Owning Site identifier.
   * @param {Object} unitData - Unit values accepted by the Unit API.
   * @returns {Promise<Object|null>} Full created Unit, or null when the originating account context is no longer current.
   */
  const addUnitToSite = useCallback(
    async (siteId, unitData) => {
      const mutationGeneration = contextGenerationRef.current;
      beginMutation();

      try {
        const response = await createUnitAPI({ ...unitData, site_id: siteId });
        if (!response?.success) {
          throw new Error(response?.message || "Failed to create unit");
        }

        if (mutationGeneration !== contextGenerationRef.current) {
          return null;
        }

        const targetSiteId = toComparableId(siteId);
        const hadActiveSiteRead = hasActiveSiteListRead();
        const activeUnitReadScope = getRelevantActiveUnitReadScope(targetSiteId);

        if (hadActiveSiteRead) {
          abortSiteListRead();
        }
        if (activeUnitReadScope) {
          abortUnitListRead();
        }

        const fullUnit = response.data;
        const unitSummary = toUnitSummary(fullUnit);

        setSites(previousSites =>
          previousSites.map(site =>
            toComparableId(site.id) === targetSiteId
              ? { ...site, units: [...(site.units || []), unitSummary] }
              : site
          )
        );

        const scope = unitsScopeRef.current;
        if (
          (scope.mode === "summary" && toComparableId(selectedSiteIdRef.current) === targetSiteId) ||
          (scope.mode === "detail" && scope.siteId === targetSiteId) ||
          scope.mode === "all-detail"
        ) {
          setUnits(previousUnits => [
            ...previousUnits,
            scope.mode === "summary" ? unitSummary : fullUnit,
          ]);
        }

        reconcileSitesAfterMutationIfNeeded(hadActiveSiteRead, mutationGeneration);
        reconcileUnitsAfterMutationIfNeeded(activeUnitReadScope, mutationGeneration);
        return fullUnit;
      } catch (mutationFailure) {
        if (mutationGeneration !== contextGenerationRef.current) {
          return null;
        }

        console.error("Error creating unit:", mutationFailure);
        setMutationError("Failed to create unit");
        throw mutationFailure;
      } finally {
        if (mutationGeneration === contextGenerationRef.current) {
          endMutation();
        }
      }
    },
    [
      abortSiteListRead,
      abortUnitListRead,
      beginMutation,
      endMutation,
      getRelevantActiveUnitReadScope,
      hasActiveSiteListRead,
      reconcileSitesAfterMutationIfNeeded,
      reconcileUnitsAfterMutationIfNeeded,
    ]
  );

  /**
   * Deletes a Unit, removes it from local summary/detail state, and reconciles only read scopes made stale by the mutation.
   *
   * @param {string|number} unitId - Unit identifier to delete.
   * @param {number} expectedVersion - Version captured with the delete target snapshot.
   * @returns {Promise<boolean>} True when deletion succeeds in the current account context; false for a stale account result.
   */
  const removeUnitFromSite = useCallback(
    async (unitId, expectedVersion) => {
      const mutationGeneration = contextGenerationRef.current;
      const targetSiteId = getUnitSiteIdFromState(unitId);
      beginMutation();

      try {
        const response = await deleteUnitAPI(unitId, expectedVersion);
        if (!response?.success) {
          throw new Error(response?.message || "Failed to delete unit");
        }

        if (mutationGeneration !== contextGenerationRef.current) {
          return false;
        }

        const hadActiveSiteRead = hasActiveSiteListRead();
        const activeUnitReadScope = getRelevantActiveUnitReadScope(targetSiteId);

        if (hadActiveSiteRead) {
          abortSiteListRead();
        }
        if (activeUnitReadScope) {
          abortUnitListRead();
        }

        setSites(previousSites =>
          previousSites.map(site => ({
            ...site,
            units: (site.units || []).filter(unit => toComparableId(unit.id) !== toComparableId(unitId)),
          }))
        );

        setUnits(previousUnits =>
          previousUnits.filter(unit => toComparableId(unit.id) !== toComparableId(unitId))
        );

        setSelectedUnit(current =>
          current && toComparableId(current.id) === toComparableId(unitId) ? null : current
        );

        reconcileSitesAfterMutationIfNeeded(hadActiveSiteRead, mutationGeneration);
        reconcileUnitsAfterMutationIfNeeded(activeUnitReadScope, mutationGeneration);
        return true;
      } catch (mutationFailure) {
        if (mutationGeneration !== contextGenerationRef.current) {
          return false;
        }

        if (mutationFailure?.response?.status === 409) {
          patchUnitConflictSnapshot(unitId, mutationFailure.response?.data?.data);
        } else if (mutationFailure?.response?.status === 404) {
          removeMissingUnitSnapshot(unitId);
        }

        console.error("Error deleting unit:", mutationFailure);
        setMutationError("Failed to delete unit");
        throw mutationFailure;
      } finally {
        if (mutationGeneration === contextGenerationRef.current) {
          endMutation();
        }
      }
    },
    [
      abortSiteListRead,
      abortUnitListRead,
      beginMutation,
      endMutation,
      getRelevantActiveUnitReadScope,
      getUnitSiteIdFromState,
      hasActiveSiteListRead,
      patchUnitConflictSnapshot,
      removeMissingUnitSnapshot,
      reconcileSitesAfterMutationIfNeeded,
      reconcileUnitsAfterMutationIfNeeded,
    ]
  );

  const restoreUnit = useCallback(async unitId => {
    const mutationGeneration = contextGenerationRef.current;
    beginMutation();
    try {
      const response = await restoreUnitAPI(unitId);
      if (!response?.success) throw new Error(response?.message || "Failed to restore Unit");
      if (mutationGeneration !== contextGenerationRef.current) return null;

      const fullUnit = response.data;
      const targetSiteId = toComparableId(fullUnit.site_id ?? fullUnit.site?.id);
      const hadActiveSiteRead = hasActiveSiteListRead();
      const activeUnitReadScope = getRelevantActiveUnitReadScope(targetSiteId);
      if (hadActiveSiteRead) {
        abortSiteListRead();
      }
      if (activeUnitReadScope) {
        abortUnitListRead();
      }

      const summary = toUnitSummary(fullUnit);
      setSites(previous => previous.map(site =>
        toComparableId(site.id) === targetSiteId
          ? {
              ...site,
              units: [
                ...(site.units || []).filter(unit => toComparableId(unit.id) !== toComparableId(fullUnit.id)),
                summary,
              ],
            }
          : site
      ));
      if (toComparableId(selectedSiteIdRef.current) === targetSiteId) {
        setUnits(previous => [
          ...previous.filter(unit => toComparableId(unit.id) !== toComparableId(fullUnit.id)),
          unitsScopeRef.current.mode === "summary" ? summary : fullUnit,
        ]);
      }
      reconcileSitesAfterMutationIfNeeded(hadActiveSiteRead, mutationGeneration);
      reconcileUnitsAfterMutationIfNeeded(activeUnitReadScope, mutationGeneration);
      return fullUnit;
    } catch (error) {
      if (mutationGeneration === contextGenerationRef.current) {
        setMutationError("Failed to restore Unit");
      }
      throw error;
    } finally {
      if (mutationGeneration === contextGenerationRef.current) endMutation();
    }
  }, [
    abortSiteListRead,
    abortUnitListRead,
    beginMutation,
    endMutation,
    getRelevantActiveUnitReadScope,
    hasActiveSiteListRead,
    reconcileSitesAfterMutationIfNeeded,
    reconcileUnitsAfterMutationIfNeeded,
  ]);

  /**
   * Updates a Unit, synchronizes local summary/detail state, and reconciles only read scopes made stale by the mutation.
   *
   * @param {string|number} unitId - Unit identifier to update.
   * @param {Object} unitData - Unit fields to persist.
   * @returns {Promise<Object|null>} Full updated Unit, or null when the originating account context is no longer current.
   */
  const updateUnitData = useCallback(
    async (unitId, unitData) => {
      const mutationGeneration = contextGenerationRef.current;
      beginMutation();

      try {
        const response = await updateUnitAPI(unitId, unitData);
        if (!response?.success) {
          throw new Error(response?.message || "Failed to update unit");
        }

        if (mutationGeneration !== contextGenerationRef.current) {
          return null;
        }

        const fullUnit = response.data;
        const targetSiteId =
          toComparableId(fullUnit?.site_id ?? fullUnit?.site?.id) ||
          getUnitSiteIdFromState(unitId);
        const hadActiveSiteRead = hasActiveSiteListRead();
        const activeUnitReadScope = getRelevantActiveUnitReadScope(targetSiteId);

        if (hadActiveSiteRead) {
          abortSiteListRead();
        }
        if (activeUnitReadScope) {
          abortUnitListRead();
        }

        const unitSummary = toUnitSummary(fullUnit);

        setSites(previousSites =>
          previousSites.map(site => {
            const siteUnits = site.units || [];
            const containsUnit = siteUnits.some(
              unit => toComparableId(unit.id) === toComparableId(unitId)
            );
            if (!containsUnit) {
              return site;
            }

            return {
              ...site,
              units: siteUnits.map(unit =>
                toComparableId(unit.id) === toComparableId(unitId)
                  ? { ...unit, ...unitSummary }
                  : unit
              ),
            };
          })
        );

        setUnits(previousUnits =>
          previousUnits.map(unit =>
            toComparableId(unit.id) === toComparableId(unitId)
              ? { ...unit, ...fullUnit }
              : unit
          )
        );

        setSelectedUnit(current =>
          current && toComparableId(current.id) === toComparableId(unitId)
            ? { ...current, ...fullUnit }
            : current
        );

        reconcileSitesAfterMutationIfNeeded(hadActiveSiteRead, mutationGeneration);
        reconcileUnitsAfterMutationIfNeeded(activeUnitReadScope, mutationGeneration);
        return fullUnit;
      } catch (mutationFailure) {
        if (mutationGeneration !== contextGenerationRef.current) {
          return null;
        }

        if (mutationFailure?.response?.status === 409) {
          patchUnitConflictSnapshot(unitId, mutationFailure.response?.data?.data);
        } else if (mutationFailure?.response?.status === 404) {
          removeMissingUnitSnapshot(unitId);
        }

        console.error("Error updating unit:", mutationFailure);
        setMutationError("Failed to update unit");
        throw mutationFailure;
      } finally {
        if (mutationGeneration === contextGenerationRef.current) {
          endMutation();
        }
      }
    },
    [
      abortSiteListRead,
      abortUnitListRead,
      beginMutation,
      endMutation,
      getRelevantActiveUnitReadScope,
      getUnitSiteIdFromState,
      hasActiveSiteListRead,
      patchUnitConflictSnapshot,
      removeMissingUnitSnapshot,
      reconcileSitesAfterMutationIfNeeded,
      reconcileUnitsAfterMutationIfNeeded,
    ]
  );

  const value = {
    selectedSite,
    selectedUnit,
    sites,
    units,
    allUnits,
    loading,
    sitesLoading,
    unitsLoading,
    mutating: mutationCount > 0,
    sitesLoaded,
    error,
    sitesError,
    unitsError,
    mutationError,
    selectSite,
    selectUnit,
    getUnitStatus,
    getSiteUnits,
    getAllUnits,
    createSite,
    deleteSite,
    restoreSite,
    getSiteById,
    addUnitToSite,
    removeUnitFromSite,
    restoreUnit,
    updateUnitData,
    updateSite,
    loadUserSites,
    loadSiteUnits,
    loadAllUnits,
  };

  return <SiteUnitContext.Provider value={value}>{children}</SiteUnitContext.Provider>;
};

export default SiteUnitContext;
